import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { GlobalService } from 'src/app/api/global.service';
import { DetailsPage as ProjectDetailsPage } from 'src/app/projects/details/details.page';
import * as dayjs from 'dayjs';

// Shown after a project template has been updated: lists the repeating schedules that were created from it and their upcoming
// projects - who each is for, which will be rebuilt from the updated template and which have been changed / worked on and will
// be left alone - and lets the accountant choose which schedules to update. Closes with
// { schedule_guids: string[], update_titles: boolean } to update those, or null for "only the template". See
// ProjectTemplateRelated.cs for what updating a schedule (and, if update_titles is set, renaming its projects) does.
@Component({
  selector: 'app-update-related-projects',
  templateUrl: './update-related-projects.component.html',
})
export class UpdateRelatedProjectsComponent implements OnInit {
  @Input() schedules: any[] = [];
  @Input() template_title = '';

  selected = new Set<string>();
  expanded = new Set<string>();
  // Off by default - existing/generated projects keep whatever title they already have unless asked to match
  // the template's (possibly just-renamed) name.
  update_titles = false;

  constructor(private modalController: ModalController, private globalService: GlobalService) {}

  ngOnInit() {
    // Every schedule is included by default - the accountant unticks anything they want left out. The project lists start
    // collapsed (the counts on each schedule already summarise them); expand a schedule to see its projects.
    for (const schedule of this.schedules) {
      if (!Array.isArray(schedule.projects)) schedule.projects = [];
      this.selected.add(schedule.schedule_guid);
    }
    this.resolveMissingNames();
  }

  // The API supplies each schedule's client or company name; if it didn't (or couldn't) look it up, it's resolved here from
  // the schedule's client / company guid the same way the rest of the app resolves names
  private async resolveMissingNames() {
    for (const schedule of this.schedules) {
      if (schedule.client_name) continue;
      try {
        if (schedule.client_guid) {
          const client = await this.globalService.GetClient(schedule.client_guid);
          if (client?.display_name) schedule.client_name = client.display_name;
        } else if (schedule.company_guid) {
          const company = await this.globalService.GetCompany(schedule.company_guid);
          if (company?.name) schedule.client_name = company.name;
        }
      } catch {
        // leave it blank - the list still shows the projects
      }
    }
  }

  isChanged(project: any): boolean {
    return project.has_changed === true || project.has_changed === 1 || project.has_changed === '1' || project.has_changed === 'true';
  }

  updatedCount(schedule: any): number {
    return schedule.projects.filter((p: any) => !this.isChanged(p)).length;
  }

  changedCount(schedule: any): number {
    return schedule.projects.filter((p: any) => this.isChanged(p)).length;
  }

  get totalToUpdate(): number {
    return this.schedules.filter(s => this.selected.has(s.schedule_guid)).reduce((sum, s) => sum + this.updatedCount(s), 0);
  }

  get totalLeftAlone(): number {
    return this.schedules.reduce((sum, s) => sum + this.changedCount(s), 0);
  }

  toggleSelected(schedule: any) {
    if (this.selected.has(schedule.schedule_guid)) this.selected.delete(schedule.schedule_guid);
    else this.selected.add(schedule.schedule_guid);
  }

  toggleExpanded(schedule: any) {
    if (this.expanded.has(schedule.schedule_guid)) this.expanded.delete(schedule.schedule_guid);
    else this.expanded.add(schedule.schedule_guid);
  }

  fmt(d: string): string {
    return d ? dayjs(d).format('MMM D, YYYY') : '—';
  }

  async viewProject(project: any) {
    const modal = await this.modalController.create({
      component: ProjectDetailsPage,
      cssClass: 'large-modal secondary-modal',
      componentProps: { guid: project.guid },
      backdropDismiss: false,
    });
    await modal.present();
  }

  confirm() {
    this.modalController.dismiss({
      schedule_guids: this.schedules.filter(s => this.selected.has(s.schedule_guid)).map(s => s.schedule_guid),
      update_titles: this.update_titles
    });
  }

  close() {
    this.modalController.dismiss(null);
  }
}
